/**
 * Pipeline FileHistory — props identity (same rules as custom-product-image-task-v2.js).
 * Shaped sections load this via {% render 'file-history-manager', history_js: 'custom-product-file-history-v2.js' %}.
 * AI twin / other pages keep component-file-history-manager.js.
 */
(() => {
  'use strict';

  const DB_NAME = window.historyDB || '3dMockupShaped_v2';
  const DB_VERSION = 1;
  const STORE_NAME = 'allaboutvibe';
  const MAX_HISTORY_SIZE = 10;

  const EVENTS = {
    CHANGED: 'fileHistoryChanged',
    READY: 'fileHistoryManagerReady',
    ERROR: 'fileHistoryManagerError'
  };

  const FileHistoryManager = (function() {
    let db = null;
    let inMemoryHistory = [];
    let config = {
      saveToIndexedDB: true
    };
    let isInitialized = false;

    // ============================================================================
    // IndexedDB Management
    // ============================================================================
    function initIndexedDB() {
      return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        
        request.onerror = () => {
          console.warn('[FileHistoryManager] IndexedDB error:', request.error);
          reject(request.error);
        };
        
        request.onsuccess = () => {
          db = request.result;
          loadFromIndexedDB()
            .then(() => resolve(db))
            .catch(reject);
        };
        
        request.onupgradeneeded = (event) => {
          const database = event.target.result;
          
          if (!database.objectStoreNames.contains(STORE_NAME)) {
            const store = database.createObjectStore(STORE_NAME, { keyPath: 'id' });
            store.createIndex('currentFile', 'currentFile', { unique: false });
          }
        };
      });
    }

    function loadFromIndexedDB() {
      return new Promise((resolve, reject) => {
        if (!db) {
          resolve();
          return;
        }
        
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.getAll();
        
        request.onsuccess = () => {
          const loaded = request.result || [];
          // Sort by createdAt descending (newest first), or by currentFile if no timestamp
          inMemoryHistory = loaded.sort((a, b) => {
            // Current file first
            if (a.currentFile && !b.currentFile) return -1;
            if (!a.currentFile && b.currentFile) return 1;
            // Then by createdAt (newest first)
            const timeA = a.createdAt || 0;
            const timeB = b.createdAt || 0;
            return timeB - timeA;
          });
          resolve();
        };
        
        request.onerror = () => {
          console.warn('[FileHistoryManager] Error loading from IndexedDB:', request.error);
          reject(request.error);
        };
      });
    }

    function saveToIndexedDB(history) {
      return new Promise((resolve, reject) => {
        if (!db) {
          resolve();
          return;
        }
        
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        
        store.clear();
        
        history.forEach(file => {
          store.add(file);
        });
        
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => {
          console.warn('[FileHistoryManager] Error saving to IndexedDB:', transaction.error);
          reject(transaction.error);
        };
      });
    }

    function clearIndexedDB() {
      if (!db) return;
      
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      store.clear();
    }

    // ============================================================================
    // Configuration
    // ============================================================================
    function setConfig(newConfig) {
      config = { ...config, ...newConfig };
      
      if (!config.saveToIndexedDB && db) {
        clearIndexedDB();
      }
      
      if (config.saveToIndexedDB && !db && isInitialized) {
        initIndexedDB().catch(error => {
          console.warn('[FileHistoryManager] Failed to init IndexedDB:', error);
        });
      }
    }

    // ============================================================================
    // History Management
    // ============================================================================
    function getHistory() {
      return [...inMemoryHistory];
    }

    function saveHistory(history) {
      const limitedHistory = history.slice(0, MAX_HISTORY_SIZE);
      
      inMemoryHistory = limitedHistory;
      
      if (config.saveToIndexedDB && db) {
        saveToIndexedDB(limitedHistory).catch(error => {
          console.warn('[FileHistoryManager] Failed to save to IndexedDB:', error);
        });
      }
    }

    function dispatchEvent(action, data) {
      window.dispatchEvent(new CustomEvent(EVENTS.CHANGED, {
        detail: { action, ...data }
      }));
    }

    // ============================================================================
    // Identity — fileOriginalId + mockup props (same rules as ImageTaskModule)
    // ============================================================================
    function identityProps(propsOrColor) {
      if (window.ImageTaskModule?.identityProps) {
        return window.ImageTaskModule.identityProps(propsOrColor);
      }
      if (propsOrColor == null || propsOrColor === '') return {};
      if (typeof propsOrColor === 'string') return { color: propsOrColor };
      if (typeof propsOrColor !== 'object') return {};
      const out = {};
      for (const key of Object.keys(propsOrColor)) {
        const value = propsOrColor[key];
        if (value == null || value === '') continue;
        out[key] = String(value);
      }
      return out;
    }

    function propsIdentitySuffix(propsOrColor) {
      if (window.ImageTaskModule?.propsIdentitySuffix) {
        return window.ImageTaskModule.propsIdentitySuffix(propsOrColor);
      }
      const props = identityProps(propsOrColor);
      const keys = Object.keys(props).sort();
      return keys.length ? keys.map((k) => props[k]).join('_') : '';
    }

    function itemProps(item) {
      if (!item) return {};
      if (item.props && typeof item.props === 'object') {
        return identityProps(item.props);
      }
      return item.color ? { color: item.color } : {};
    }

    function propsMatch(a, b) {
      const pa = identityProps(a);
      const pb = identityProps(b);
      const keys = new Set([...Object.keys(pa), ...Object.keys(pb)]);
      if (!keys.size) return false;
      for (const key of keys) {
        if (pa[key] !== pb[key]) return false;
      }
      return true;
    }

    function generateFileOriginalId(name, size, lastModified) {
      if (!name || typeof size !== 'number' || typeof lastModified !== 'number') {
        return null;
      }
      return `${name}_${size}_${lastModified}`;
    }

    /** @param {Object|string} propsOrColor */
    function generateFileId(fileOriginalId, propsOrColor) {
      if (window.ImageTaskModule?.generateTaskId) {
        return window.ImageTaskModule.generateTaskId(fileOriginalId, propsOrColor);
      }
      const suffix = propsIdentitySuffix(propsOrColor);
      if (!fileOriginalId || !suffix) return null;
      return `${fileOriginalId}_${suffix}`;
    }

    // ============================================================================
    // File Operations
    // ============================================================================
    function getCurrentFile() {
      const history = getHistory();
      return history.find(f => f.currentFile === true) || null;
    }

    function setCurrentFile(fileId) {
      const history = getHistory();
      let changed = false;
      
      history.forEach(f => {
        if (f.currentFile && f.id !== fileId) {
          f.currentFile = false;
          changed = true;
        } else if (!f.currentFile && f.id === fileId) {
          f.currentFile = true;
          changed = true;
        }
      });
      
      if (changed) {
        saveHistory(history);
        dispatchEvent('currentChanged', {
          currentFileId: fileId
        });
      }
      
      return history.find(f => f.id === fileId) || null;
    }

    function getFileFromHistory(fileId) {
      const history = getHistory();
      return history.find(f => f.id === fileId) || null;
    }

    /** @param {Object|string} propsOrColor */
    function getFileFromHistoryByUrl(s3Url, propsOrColor) {
      const history = getHistory();
      const target = identityProps(propsOrColor);
      if (!s3Url || !Object.keys(target).length) return null;
      return history.find(f => f.s3Url === s3Url && propsMatch(itemProps(f), target)) || null;
    }

    function getFileByOriginalId(originalFileId) {
      if (!originalFileId) return null;
      const history = getHistory();
      return history.find(f => {
        if (f.fileOriginalId === originalFileId) return true;
        const props = itemProps(f);
        const suffix = propsIdentitySuffix(props);
        if (f.id && suffix && f.id.endsWith(`_${suffix}`)) {
          return f.id.slice(0, -(`_${suffix}`.length)) === originalFileId;
        }
        return false;
      }) || null;
    }

    /** @param {Object|string} propsOrColor */
    function isFileInHistory(fileOriginalId, url = null, propsOrColor = null) {
      const history = getHistory();
      const props = identityProps(propsOrColor);
      if (!Object.keys(props).length) return false;

      if (fileOriginalId) {
        const fileId = generateFileId(fileOriginalId, props);
        if (fileId && history.some(f => f.id === fileId)) {
          return true;
        }
      }

      if (url) {
        return history.some(f => f.s3Url === url && propsMatch(itemProps(f), props));
      }

      return false;
    }

    function createHistoryItemObject(data) {
      const props = identityProps(data.props || (data.color ? { color: data.color } : {}));
      return {
        id: data.id,
        fileOriginalId: data.fileOriginalId,
        props: { ...props },
        color: props.color || data.color || null,
        s3Url: data.s3Url,
        originalS3Url: data.originalS3Url || null,
        preview: data.preview,
        previewWithBg: data.previewWithBg || data.preview,
        pillowData: data.pillowData,
        productType: data.productType || null,
        currentFile: true,
        createdAt: data.createdAt || Date.now()
      };
    }

    function createHistoryItem(data) {
      if (!data || typeof data !== 'object') {
        console.warn('[FileHistoryManager] createHistoryItem: data object is required');
        return null;
      }

      const props = identityProps(data.props || (data.color ? { color: data.color } : {}));
      if (!Object.keys(props).length) {
        console.warn('[FileHistoryManager] createHistoryItem: props (or color) is required');
        return null;
      }

      const requiredFields = ['id', 'fileOriginalId', 's3Url', 'preview', 'pillowData', 'previewWithBg'];
      for (const field of requiredFields) {
        if (!data[field]) {
          console.warn(`[FileHistoryManager] createHistoryItem: ${field} is required`);
          return null;
        }
      }

      const payload = { ...data, props, color: props.color || data.color || null };

      const existingItem = getFileFromHistory(payload.id);
      if (existingItem) {
        existingItem.createdAt = Date.now();
        existingItem.currentFile = true;
        if (payload.pillowData) existingItem.pillowData = payload.pillowData;
        if (payload.preview) existingItem.preview = payload.preview;
        if (payload.previewWithBg) existingItem.previewWithBg = payload.previewWithBg;
        if (payload.s3Url) existingItem.s3Url = payload.s3Url;
        if (payload.originalS3Url) existingItem.originalS3Url = payload.originalS3Url;
        if (payload.fileOriginalId) existingItem.fileOriginalId = payload.fileOriginalId;
        if (payload.productType) existingItem.productType = payload.productType;
        existingItem.props = { ...props };
        existingItem.color = props.color || null;
        setCurrentFile(payload.id);
        const history = getHistory();
        history.sort((a, b) => {
          if (a.currentFile && !b.currentFile) return -1;
          if (!a.currentFile && b.currentFile) return 1;
          const timeA = a.createdAt || 0;
          const timeB = b.createdAt || 0;
          return timeB - timeA;
        });
        saveHistory(history);
        return existingItem;
      }

      // Same design under a new id scheme (e.g. hash vs legacy name_size) — don't fork a copy
      const existingByUrl = getFileFromHistoryByUrl(payload.s3Url, props);
      if (existingByUrl) {
        existingByUrl.createdAt = Date.now();
        existingByUrl.currentFile = true;
        if (payload.pillowData) existingByUrl.pillowData = payload.pillowData;
        if (payload.preview) existingByUrl.preview = payload.preview;
        if (payload.previewWithBg) existingByUrl.previewWithBg = payload.previewWithBg;
        if (payload.originalS3Url) existingByUrl.originalS3Url = payload.originalS3Url;
        if (payload.productType) existingByUrl.productType = payload.productType;
        existingByUrl.props = { ...props };
        existingByUrl.color = props.color || null;
        setCurrentFile(existingByUrl.id);
        const history = getHistory();
        history.sort((a, b) => {
          if (a.currentFile && !b.currentFile) return -1;
          if (!a.currentFile && b.currentFile) return 1;
          const timeA = a.createdAt || 0;
          const timeB = b.createdAt || 0;
          return timeB - timeA;
        });
        saveHistory(history);
        return existingByUrl;
      }

      const history = getHistory();
      const historyItem = createHistoryItemObject(payload);

      const existingIndex = history.findIndex(f => f.id === historyItem.id);
      if (existingIndex !== -1) {
        history.splice(existingIndex, 1);
      }

      history.forEach(f => f.currentFile = false);
      history.unshift(historyItem);

      saveHistory(history);
      dispatchEvent('added', { fileInfo: historyItem });

      return historyItem;
    }

    /**
     * Lookup cached mockup for file + identity props.
     * @param {string|null} fileOriginalId
     * @param {string|null} fileUrl
     * @param {Object|string|null} propsOrColor - props object or legacy color string
     */
    function useFromHistory(fileOriginalId, fileUrl = null, propsOrColor = null) {
      const props = identityProps(propsOrColor);
      const empty = {
        fileId: null,
        s3Url: null,
        originalS3Url: null,
        pillowData: null,
        preview: null,
        previewWithBg: null,
        props: {},
        color: null
      };
      if (!Object.keys(props).length) {
        return empty;
      }

      const isInHistory = isFileInHistory(fileOriginalId, fileUrl, props);
      let result = { ...empty };

      if (!isInHistory && fileOriginalId) {
        const fileInfoByOriginalId = getFileByOriginalId(fileOriginalId);
        if (fileInfoByOriginalId) {
          result = {
            fileId: null,
            s3Url: fileInfoByOriginalId.s3Url || null,
            originalS3Url: fileInfoByOriginalId.originalS3Url || null,
            pillowData: null,
            preview: null,
            previewWithBg: null,
            fileOriginalId: fileInfoByOriginalId.fileOriginalId || null,
            props: {},
            color: null
          };
        }
      } else {
        let fileInfo = null;

        if (fileOriginalId) {
          fileInfo = getFileFromHistory(generateFileId(fileOriginalId, props));
        } else if (fileUrl) {
          fileInfo = getFileFromHistoryByUrl(fileUrl, props);
        }

        if (fileInfo) {
          const ip = itemProps(fileInfo);
          result = {
            fileId: fileInfo.id || null,
            s3Url: fileInfo.s3Url || null,
            originalS3Url: fileInfo.originalS3Url || null,
            pillowData: fileInfo.pillowData || null,
            preview: fileInfo.preview || null,
            previewWithBg: fileInfo.previewWithBg || null,
            fileOriginalId: fileInfo.fileOriginalId || null,
            props: { ...ip },
            color: ip.color || fileInfo.color || null
          };
        }
      }

      if (result.fileId) {
        setCurrentFile(result.fileId);
      }

      return result;
    }

    // ============================================================================
    // Initialization
    // ============================================================================
    function init() {
      if (isInitialized) return;
      
      if (config.saveToIndexedDB) {
        initIndexedDB()
          .then(() => {
            isInitialized = true;
            window.dispatchEvent(new CustomEvent(EVENTS.READY, {
              detail: {
                manager: 'FileHistoryManager',
                status: 'ready',
                hasIndexedDB: true,
                historyCount: inMemoryHistory.length
              }
            }));
          })
          .catch(error => {
            console.warn('[FileHistoryManager] Initialization failed:', error);
            window.dispatchEvent(new CustomEvent(EVENTS.ERROR, {
              detail: {
                manager: 'FileHistoryManager',
                status: 'error',
                error: error.message,
                hasIndexedDB: false
              }
            }));
          });
      } else {
        isInitialized = true;
        window.dispatchEvent(new CustomEvent(EVENTS.READY, {
          detail: {
            manager: 'FileHistoryManager',
            status: 'ready',
            hasIndexedDB: false,
            historyCount: inMemoryHistory.length
          }
        }));
      }
    }
     // Clear all history
     function clearHistory() {
      try {
          if (saveToIndexedDB && db) {
              try {
                  clearIndexedDB();
              } catch (error) {
                  console.warn('Error clearing IndexedDB:', error);
              }
          }
          if (saveInMemory) {
              inMemoryHistory = [];
          }
      } catch (error) {
          console.warn('clearHistory error:', error.message);
      }
  }

    return {
      setConfig,
      init,
      getHistory,
      getCurrentFile,
      setCurrentFile,
      getFileFromHistory,
      getFileFromHistoryByUrl,
      getFileByOriginalId,
      isFileInHistory,
      useFromHistory,
      createHistoryItem,
      clearHistory
    };
  })();

  // ============================================================================
  // Auto-initialize
  // ============================================================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => FileHistoryManager.init());
  } else {
    FileHistoryManager.init();
  }

  // ============================================================================
  // Expose API
  // ============================================================================
  window.FileHistoryManager = FileHistoryManager;
  window.FileHistoryManager.EVENTS = EVENTS;

  window.ShapedFileHistory = {
    setConfig: (config) => FileHistoryManager.setConfig(config),
    init: () => FileHistoryManager.init(),
    getHistory: () => FileHistoryManager.getHistory(),
    getCurrentFile: () => FileHistoryManager.getCurrentFile(),
    setCurrentFile: (fileId) => FileHistoryManager.setCurrentFile(fileId),
    getFileFromHistory: (fileId) => FileHistoryManager.getFileFromHistory(fileId),
    getFileFromHistoryByUrl: (fileUrl, color) => FileHistoryManager.getFileFromHistoryByUrl(fileUrl, color),
    getFileByOriginalId: (originalFileId) => FileHistoryManager.getFileByOriginalId(originalFileId),
    isFileInHistory: (file, fileUrl, color) => FileHistoryManager.isFileInHistory(file, fileUrl, color),
    useFromHistory: (fileOriginalId, fileUrl, color) => FileHistoryManager.useFromHistory(fileOriginalId, fileUrl, color),
    createHistoryItem: (data) => FileHistoryManager.createHistoryItem(data),
    clearHistory: () => FileHistoryManager.clearHistory()
  };
})();
